import React, { useState, useEffect, useRef,useMemo,useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser,
  faEnvelope,
  faGlobe,
  faRobot,
  faUsers,
  faCheck,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin,faFacebook, faGoogle, faYahoo, faMicrosoft  } from '@fortawesome/free-brands-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { getUsers } from '../services/api/courseService'; // Import the getUsers function
import AddUserDialog from '../components/auth/AddUserDialog'; // Import AddUserDialog component
import { checkIsValidUser } from '../services/api/userService'; // Import the checkIsValidUser function
import { CanvasUser } from '../types/CanvasUser';
import { stringify } from 'querystring';
import { Settings } from 'lucide-react';


interface LocalUser {
  name: string;
  netId: string;
  role: string;
}

interface SocialLoginOption {
  name: string;
  enabled: boolean;
  icon: IconDefinition;
}

interface AuthSchemes {
  canvasUsers: boolean;
  localUsers: boolean;
  socialLogins: boolean;
}

interface CourseIdentifier {
  course_id: string;
  course_url?: string;
}

interface AuthenticationSettingsProps {
  isAuthenticationEnabled: boolean;
  canvasUserEnabled: boolean;
  localUserEnabled: boolean;
  socialLoginEnabled: boolean;
  everyOneEnabled: boolean;
  canvas_users_excluded: string[]; 
  local_users_included: { name: string;netId: string;role: string; }[];  
  onSettingsChange: (updatedSettings: {
    isAuthenticationEnabled?: boolean;
    canvasUserEnabled?: boolean;
    localUserEnabled?: boolean;
    socialLoginEnabled?: boolean;
    everyOneEnabled?: boolean;
    selectedSocialLogins?: string[];
    canvas_users_excluded?: string[]; 
    local_users_included?: { name: string;netId: string;role: string; }[]; 
  }) => void;
  courseIds: CourseIdentifier[];// Add this prop to receive course IDs
  selectedSocialLogins: string[]; 
}

const AuthenticationSettings: React.FC<AuthenticationSettingsProps> = ({
  isAuthenticationEnabled,
  canvasUserEnabled,
  localUserEnabled,
  socialLoginEnabled,
  everyOneEnabled,
  canvas_users_excluded, 
  local_users_included,
  onSettingsChange,
  courseIds, // Receive course IDs
  selectedSocialLogins
}) => {
  const prevCourseIdsRef = useRef<CourseIdentifier[]>([]);
  const [authEnabled, setAuthEnabled] = useState<boolean>(isAuthenticationEnabled);
  const [showWarning, setShowWarning] = useState(false);

  const handleAuthenticationToggle = (value: boolean) => {
    setAuthEnabled(value);
    onSettingsChange({ isAuthenticationEnabled: value });
    if (value === false) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };
  
  const [schemes, setSchemes] = useState({
    canvasUsers: canvasUserEnabled,
    localUsers: localUserEnabled,
    socialLogins: socialLoginEnabled,
  });
  const labelMap: Record<string, string> = {
  canvasUsers: 'People enrolled in my Canvas course(s)',
  localUsers: 'Local Users that I specify from within my organization',
  socialLogins: 'Social Logins',
};


  // const [is_everyone, setEveryone] = useState(everyOneEnabled);
  const [is_everyone, setEveryone] = useState(everyOneEnabled === true);
 
  // Handle "Everyone" checkbox change

  const handleEveryoneChange = () => {
  const newValue = !is_everyone; // Calculate new value first
  setEveryone(newValue);
  
  // Update the everyone setting
  onSettingsChange({ everyOneEnabled: newValue });
  
  if (newValue === true) {
    // Uncheck all other auth schemes when "Everyone" is checked
    setSchemes({
      canvasUsers: false,
      localUsers: false,
      socialLogins: false,
    });
   
    onSettingsChange({
      canvasUserEnabled: false,
      localUserEnabled: false,
    });
  }
};
  const [canvasUsers, setCanvasUsers] = useState<CanvasUser[]>([]);

  const [localUsers, setLocalUsers] = useState<LocalUser[]>(local_users_included);

  const [socialLoginOptions, setSocialLoginOptions] = useState<SocialLoginOption[]>([
    { name: 'LinkedIn', enabled: false, icon: faLinkedin },
    { name: 'Google', enabled: false, icon: faGoogle },
    { name: 'Yahoo', enabled: false, icon: faYahoo },
    { name: 'Facebook', enabled: false, icon: faFacebook },
    { name: 'Microsoft', enabled: false, icon: faMicrosoft }
  ]);

  const [showAddUserDialog, setShowAddUserDialog] = useState(false);

  // useEffect(() => {
  //   console.log("courseIds has changed:", courseIds);
  //   // You can perform any action you need whenever courseIds changes
  // }, [courseIds]); 

  useEffect(() => {
    setAuthEnabled(isAuthenticationEnabled);
    setSchemes({
      canvasUsers: canvasUserEnabled,
      localUsers: localUserEnabled,
      socialLogins: socialLoginEnabled,
    });
    const val = typeof everyOneEnabled === 'boolean' 
    ? everyOneEnabled 
    : everyOneEnabled === 'true'
    setEveryone(val)
  }, [isAuthenticationEnabled, canvasUserEnabled, localUserEnabled, socialLoginEnabled,everyOneEnabled]);
  
   // Auto-hide warning after 5 seconds
  useEffect(() => {
    let timer:any;
    if (showWarning) {
      timer = setTimeout(() => {
        setShowWarning(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [showWarning]);

  const fetchAndAddCanvasUsers = useCallback(async (courseIdentifiers: CourseIdentifier[]) => {
    // console.log("Fetching Canvas users...");
    try {
        const response = await getUsers(courseIdentifiers);
        if (response) {
            const newUsers = response.map((user: any) => ({
                name: user.name,
                id: user.sis_user_id || user.id,
                enabled: true,
                course_name: user.course_name,
                role: user.role
            }));
            setCanvasUsers(newUsers);
        }
    } catch (error) {
        console.error("Error fetching Canvas users:", error);
    }
}, []);

useEffect(() => {
  if (!courseIds.length) {
    setCanvasUsers([]); 
    return; // Exit early if no courseIds
  }

  // const isCourseIdsChanged =
  //     prevCourseIdsRef.current.length !== courseIds.length ||
  //     !courseIds.every((id, index) => id === prevCourseIdsRef.current[index]);
  // console.log("isCourseIdsChanged"+isCourseIdsChanged)
  // if (isCourseIdsChanged) {
      // console.log("Fetching new Canvas users for updated courseIds:", courseIds);
      fetchAndAddCanvasUsers(courseIds);
      prevCourseIdsRef.current = [...courseIds]; // Update stored courseIds
  // }
}, [courseIds, fetchAndAddCanvasUsers]);


  

  const handleAddLocalUser = async (userId: string): Promise<{ valid: boolean }> => {
    if (localUsers.some(user => user.netId === userId)) {
      return { valid: false };
    }
    try {
      const response = await checkIsValidUser(userId);
      if (response.valid) {
        const newUser: LocalUser = {
          name: response.name,
          netId: response.netId,
          role: 'User'
        };
        setLocalUsers((prevUsers) => {
          const updatedUsers = [...prevUsers, newUser];
          // Now, update local_users_included to the parent component
          onSettingsChange({
            local_users_included: updatedUsers,
          });
          return updatedUsers;
        });
        return { valid: true };
      } else {
        return { valid: false };
      }
    } catch (error) {
      console.error('Error checking user validity:', error);
      return { valid: false };
    }
  };

  const handleDeleteLocalUser = (netId: string): void => {
    setLocalUsers((prevUsers) => {
      const updatedUsers = prevUsers.filter(user => user.netId !== netId);
      
      // Now, update local_users_included in the parent component with the new list
      onSettingsChange({
        local_users_included: updatedUsers,
      });
  
      return updatedUsers; // Return the updated users list for state update
    });
  };

  const [checkedUsers, setCheckedUsers] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
  // Initialize checkedUsers state based on canvasUsers
  const initialCheckedState: { [key: string]: boolean } = {};
  canvasUsers.forEach(user => {
    initialCheckedState[user.id] = !canvas_users_excluded.includes(user.id);
  });
  setCheckedUsers(initialCheckedState);
}, [canvasUsers, canvas_users_excluded]);

const toggleCanvasUserAccess = (userId: string): void => {
  setCheckedUsers(prevCheckedUsers => {
    const updatedCheckedUsers = {
      ...prevCheckedUsers,
      [userId]: !prevCheckedUsers[userId] // Toggle the state
    };

    // Update the excluded users list
    const uncheckedCanvasUserIds = Object.keys(updatedCheckedUsers)
      .filter(id => !updatedCheckedUsers[id]);

    // Pass the unchecked users to onSettingsChange
    onSettingsChange({
      canvasUserEnabled: Object.values(updatedCheckedUsers).some(Boolean),
      canvas_users_excluded: uncheckedCanvasUserIds,
    });

    return updatedCheckedUsers;
  });
};
  

  const toggleSocialLogin = (index: number): void => {
      const updatedOptions = [...socialLoginOptions];
      updatedOptions[index].enabled = !updatedOptions[index].enabled;
      setSocialLoginOptions(updatedOptions);

      // Get selected social logins
      const selectedSocialLogins = updatedOptions
        .filter(option => option.enabled)
        .map(option => option.name);

      onSettingsChange({ 
        socialLoginEnabled: updatedOptions.some(option => option.enabled),
        selectedSocialLogins, // Pass enabled social logins
      });
  };

  const handleSchemeChange = (key: keyof AuthSchemes) => (e: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedSchemes = { ...schemes, [key]: e.target.checked };
    setSchemes(updatedSchemes);
  
    // Map the schemes to match the expected structure for onSettingsChange
    const updatedSettings: {
      canvasUserEnabled?: boolean;
      localUserEnabled?: boolean;
      socialLoginEnabled?: boolean;
      canvas_users_excluded?: string[];
      local_users_included?: { name: string;netId: string;role: string; }[]; 
      selectedSocialLogins?:string[];

    } = {
      canvasUserEnabled: updatedSchemes.canvasUsers,
      localUserEnabled: updatedSchemes.localUsers,
      socialLoginEnabled: updatedSchemes.socialLogins,
    };

    if (updatedSchemes.canvasUsers === false) {
      updatedSettings.canvas_users_excluded = []; // Reset excluded Canvas users
    }
    
    if (updatedSchemes.localUsers === false) {
      updatedSettings.local_users_included = []; // Reset excluded Canvas users
    }
    if (updatedSchemes.socialLogins === false) {
      updatedSettings.selectedSocialLogins = []; // Reset excluded Canvas users
    }
  
    onSettingsChange(updatedSettings);
  };

  const handleCheckboxChange = (key: string, value: boolean) => {
    onSettingsChange({ [key]: value });
  };

  const CustomCheckbox = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <div 
      className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
        String(checked) === 'true' ? "bg-black" : "bg-white"
      }`}
      onClick={onChange}
    >
      {String(checked) === 'true' && (
        <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-white" />
      )}
    </div>
  );

  const CustomCheckboxCanvas = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <div 
      className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
        checked ? "bg-black" : "bg-white"
      }`}
      onClick={onChange}
    >
      {checked && (
        <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-white" />
      )}
    </div>
  );

  const CustomRadio = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <div 
      className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
        checked ? "bg-black" : "bg-white"
      }`}
      onClick={onChange}
    >
      {checked && (
        <div className="h-2 w-2 bg-white rounded-full" />
      )}
    </div>
  );

  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(ITEMS_PER_PAGE);  // Default page size is 5
  const [searchQuery, setSearchQuery] = useState<string>(''); 
  
// Filter canvas users based on the search query
const filteredCanvasUsers = useMemo(() => {
  return canvasUsers.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.id.toLowerCase().includes(searchQuery.toLowerCase())
  );
}, [canvasUsers, searchQuery]);

  // Compute the paginated users
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCanvasUsers.slice(start, start + pageSize);
  }, [filteredCanvasUsers, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredCanvasUsers.length / pageSize);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when search query changes
  };

  return (
    <div className="w-full space-y-6 p-10">
      {/* Authentication Toggle */}
      {/* Authentication Toggle */}
      <div key={`auth-${authEnabled}`} className="space-y-2">   {/* Force re-render on state change */}
  <h3  className="text-2xl font-bold mb-4">
    Authentication
    {/* { String(authEnabled) == 'false' } */}
  </h3>
  <label className="block pt-1 pb-2 text-sm font-medium text-gray-700">
    Users must login to use this Chatbot
  </label>
  <div className="flex gap-4">
    {/* Yes */}
    <label className="flex items-center space-x-2 cursor-pointer">
      <div 
        className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
          String(authEnabled) === 'true' ? "bg-black" : "bg-white"
        }`}
        onClick={() => handleAuthenticationToggle(true)}
      >
        {String(authEnabled) === 'true' && <div className="h-2 w-2 bg-white rounded-full" />}
      </div>
      <span>Yes</span>
    </label>

    {/* No */}
    <label className="flex items-center space-x-2 cursor-pointer">
      <div 
        className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
          String(authEnabled) === 'false' ? "bg-black" : "bg-white"
        }`}
        onClick={() => handleAuthenticationToggle(false)}
      >
        {String(authEnabled) === 'false' && <div className="h-2 w-2 bg-white rounded-full" />}
      </div>
      <span>No</span>
    </label>
    <label> (Public; not recommended)</label>
  </div>
  {/* Warning message */}
  {showWarning && (
    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 text-sm animate-fade-in">
      Selecting "No" will make your Chatbot publicly accessible to anyone who has the link.
    </div>
  )}
</div>



      {String(authEnabled) === 'true' && (
        <>
          {/* Authentication Scheme */}
            <div className="space-y-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">

              {/* Everyone Option - visually distinct */}
               <label className="flex items-center space-x-3 transition-colors cursor-pointer">
                                               <input
                                                 type="checkbox"
                                                 checked={is_everyone}
                                                 onChange={handleEveryoneChange}
                                                 className="hidden"
                                               />
                                               <div
                                                 className={`mr-2 h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center ${
                                                   is_everyone ? "bg-black" : "bg-white"
                                                 }`}
                                               >
                                                 {is_everyone && (
                                                   <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-white" />
                                                 )}
                                               </div>
                                               <span className="font-medium text-black">Anyone</span>
                                             </label>

            {/* OR separator */}
            {/* <div className="text-sm text-gray-500 text-center tracking-wide">OR SELECT A USER BELOW</div> */}

            {/* Auth Schemes */}
            <div className="space-y-2 pl-1">
              {Object.entries(schemes).map(([key, value]) => {
                const is_everyone_checked = String(is_everyone) === 'true'
                const isDisabled = (key === "socialLogins" ||  is_everyone_checked);

                return (
                  <label
                    key={key}
                    className={`flex items-center space-x-3 p-2 rounded-md transition-colors duration-150 ${
                      isDisabled
                        ? 'opacity-50 cursor-not-allowed bg-gray-50'
                        : 'hover:bg-gray-100 cursor-pointer'
                    }`}
                  >
                    <CustomCheckbox
                      checked={value}
                      onChange={() => {
                        if (!isDisabled) {
                          handleSchemeChange(key as keyof AuthSchemes)({
                            target: { checked: !value },
                          } as React.ChangeEvent<HTMLInputElement>);
                        }
                      }}
                    />
                    <span className="text-gray-800">
                     {labelMap[key] || key}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>


          {/* Canvas Users Table */}
          {String(schemes.canvasUsers) === 'true' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Canvas Users</h3>
              <p className="text-sm text-gray-600 mb-4">
                If you are getting data from your Canvas course site, by default, people in that course will automatically have access to your Chatbot. You can choose to allow or disallow access to individual Canvas users. When a user gets enrolled in your Canvas course site, he/she will get added to this list after the Canvas data pipeline runs.
              </p>
              <div className="overflow-x-auto">
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="text"
                  id="searchCanvasUsers"
                  value={searchQuery}
                  onChange={handleSearchChange} // Update the search query
                  placeholder="Search by name or SIS ID"
                  className={`mt-3 ml-1 w-[40%] p-2 border border-gray-300 rounded-lg shadow-sm focus:ring-0 focus:border-gray-500 focus:outline-none sm:text-sm` }
                  
                />
              </div>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="table-header">Name</th>
                      <th className="table-header">Canvas SIS ID</th>
                      <th className="table-header">Course Name</th>
                      <th className="table-header">User Role</th>
                      <th className="table-header">Allow Access</th>
                    </tr>
                  </thead>
                  <tbody>
                  {paginatedUsers.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-left">{user.name}</td>
                        <td className="p-3 text-left">{user.id}</td>
                        <td
                          className="p-3 text-left truncate max-w-[250px]"
                          title={user.course_name}
                        >
                          {user.course_name}
                        </td>

                        <td className="p-3 text-left">{user.role}</td>
                        <td className="p-3 text-left">
                          <div className="flex justify-left pl-6">
                            <div
                              className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
                                checkedUsers[user.id] ? 'bg-black' : 'bg-white'
                              }`}
                              onClick={() => toggleCanvasUserAccess(user.id)}
                              id={`checkbox-${user.id}`}
                            >
                              {checkedUsers[user.id] && <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-white" />}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}


          </tbody>
                </table>
              </div>
              {/* Pagination Controls */}
      <div className="flex justify-center items-center gap-2 mt-4">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
        >
          Previous
        </button>

        {Array.from({ length: totalPages }, (_, i) => {
          const pageNumber = i + 1;
          const isFirst = pageNumber === 1;
          const isLast = pageNumber === totalPages;
          const isInRange = pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2;

          if (isFirst || isLast || isInRange) {
            return (
              <button
                key={pageNumber}
                onClick={() => setCurrentPage(pageNumber)}
                className={`px-3 py-1 border rounded-md ${
                  currentPage === pageNumber ? "bg-gray-900 text-white" : "hover:bg-gray-50"
                }`}
              >
                {pageNumber}
              </button>
            );
          }

          if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
            return <span key={`ellipsis-${pageNumber}`}>...</span>;
          }

          return null;
        })}

        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
        >
          Next
        </button>
        <div className="flex items-center gap-2 ml-4">
        <label htmlFor="pageSize" className="text-sm font-medium text-gray-900">Page Size:</label>
        <select
          id="pageSize"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="px-2 py-1 border border-gray-300 rounded-md"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
          <option value={50}>50</option>
        </select>
      </div>
    
      </div>
            </div>
          )}

          {/* Local Users Table */}
          {String(schemes.localUsers) === 'true' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Local Users</h3>
              <div className="overflow-x-auto">
                {localUsers.length === 0 ? (
                  <div className="flex justify-center items-center h-24">
                    <p className="text-gray-600">No local users added for this chatbot.</p>
                  </div>
                ) : (
                  <table className="w-full border-collapse mb-4">
                    <thead>
                      <tr className="border-b">
                        <th className="table-header">Name</th>
                        <th className="table-header">NetId</th>
                        {/* <th className="table-header">Role</th> */}
                        <th className="table-header">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localUsers.map((user, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-left">{user.name}</td>
                          <td className="p-3 text-left">{user.netId}</td>
                          {/* <td className="p-3 text-left">{user.role}</td> */}
                          <td className="p-3 text-left pl-7">
                            <button 
                              className="text-black hover:text-red-800"
                              onClick={() => handleDeleteLocalUser(user.netId)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <button
                  onClick={() => setShowAddUserDialog(true)}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                >
                  Add User
                </button>
              </div>
            </div>
          )}
          {/* Add User Dialog */}
          <AddUserDialog
            isOpen={showAddUserDialog}
            onClose={() => setShowAddUserDialog(false)}
            onAddUser={handleAddLocalUser}
          />
          {/* Social Logins */}
          {/* Social Logins */}
{String(schemes.socialLogins) === 'true' && (
  <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <h3 className="text-lg font-semibold mb-4">Social Logins</h3>

    {/* Overlay with "Coming Soon" text */}
    <div className="absolute inset-0 bg-gray-200 bg-opacity-50 flex items-center justify-center text-black text-lg font-semibold" style={{ pointerEvents: 'none' }}>
      Coming Soon...
    </div>

    <div className="space-y-3 opacity-50 pointer-events-none"> 
      {socialLoginOptions.map((option, idx) => (
        <div
          key={idx}
          className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
        >
          <CustomCheckbox
            checked={selectedSocialLogins.includes(option.name)}  
            onChange={() => toggleSocialLogin(idx)}
          />
          <FontAwesomeIcon 
            icon={option.icon} 
            className="w-5 h-5 text-gray-600" 
          />
          <span className="text-gray-700">{option.name}</span>
        </div>
      ))}
    </div>
  </div>
)}

        </>
      )}
    </div>
  );
};

export default AuthenticationSettings;
